import React, { useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { Box, IconButton, Tooltip, Divider, CircularProgress } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  Title,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Code,
  HorizontalRule,
  Link as LinkIcon,
  LinkOff,
  Image as ImageIcon,
  Undo,
  Redo,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify
} from '@mui/icons-material';
import './RichTextEditor.css';

/**
 * Rich text editor for blog content. Notifies the parent on every change with
 * both HTML (for storage/render) and JSON (so re-editing preserves structure).
 */
const RichTextEditor = ({
  initialHtml = '',
  placeholder = 'Start writing your post…',
  onChange,
  onImageUpload // (file) => Promise<{ url }>
}) => {
  const fileInputRef = useRef(null);
  const uploadingRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] }
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'nofollow noopener noreferrer', target: '_blank' }
      }),
      Image.configure({ HTMLAttributes: { class: 'rte-image' } }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ['heading', 'paragraph'] })
    ],
    content: initialHtml || '',
    onUpdate: ({ editor: ed }) => {
      if (typeof onChange === 'function') {
        onChange({ html: ed.getHTML(), json: ed.getJSON() });
      }
    }
  });

  // Sync external content changes (e.g. when loading an existing blog after the
  // editor has already mounted with empty content).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (initialHtml && initialHtml !== current) {
      editor.commands.setContent(initialHtml, false);
    }
  }, [editor, initialHtml]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const triggerImagePick = useCallback(() => {
    if (!editor) return;
    if (typeof onImageUpload !== 'function') {
      const url = window.prompt('Image URL');
      if (url) editor.chain().focus().setImage({ src: url }).run();
      return;
    }
    fileInputRef.current?.click();
  }, [editor, onImageUpload]);

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !editor || uploadingRef.current) return;
      try {
        uploadingRef.current = true;
        const { url } = await onImageUpload(file);
        if (url) {
          editor.chain().focus().setImage({ src: url, alt: file.name }).run();
        }
      } catch (err) {
        window.alert(err?.response?.data?.message || err?.message || 'Image upload failed');
      } finally {
        uploadingRef.current = false;
      }
    },
    [editor, onImageUpload]
  );

  if (!editor) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const Btn = ({ title, onClick, active, disabled, children }) => (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            color: active ? '#3265a8' : '#4b5563',
            backgroundColor: active ? '#e8f0fb' : 'transparent',
            borderRadius: 1.25,
            '&:hover': { backgroundColor: '#f3f4f6', color: '#1f2937' }
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box className="rte-wrapper">
      <Box className="rte-toolbar">
        <Btn
          title="Heading 1"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive('heading', { level: 1 })}
        >
          <Title fontSize="small" />
        </Btn>
        <Btn
          title="Heading 2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
        >
          <span style={{ fontWeight: 700, fontSize: 13 }}>H2</span>
        </Btn>
        <Btn
          title="Heading 3"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
        >
          <span style={{ fontWeight: 700, fontSize: 13 }}>H3</span>
        </Btn>
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <Btn
          title="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
        >
          <FormatBold fontSize="small" />
        </Btn>
        <Btn
          title="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
        >
          <FormatItalic fontSize="small" />
        </Btn>
        <Btn
          title="Underline"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
        >
          <FormatUnderlined fontSize="small" />
        </Btn>
        <Btn
          title="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
        >
          <StrikethroughS fontSize="small" />
        </Btn>
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <Btn
          title="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
        >
          <FormatListBulleted fontSize="small" />
        </Btn>
        <Btn
          title="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
        >
          <FormatListNumbered fontSize="small" />
        </Btn>
        <Btn
          title="Quote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
        >
          <FormatQuote fontSize="small" />
        </Btn>
        <Btn
          title="Code block"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive('codeBlock')}
        >
          <Code fontSize="small" />
        </Btn>
        <Btn
          title="Horizontal rule"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <HorizontalRule fontSize="small" />
        </Btn>
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <Btn
          title="Align left"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
        >
          <FormatAlignLeft fontSize="small" />
        </Btn>
        <Btn
          title="Align center"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
        >
          <FormatAlignCenter fontSize="small" />
        </Btn>
        <Btn
          title="Align right"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
        >
          <FormatAlignRight fontSize="small" />
        </Btn>
        <Btn
          title="Justify"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          active={editor.isActive({ textAlign: 'justify' })}
        >
          <FormatAlignJustify fontSize="small" />
        </Btn>
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <Btn title="Link" onClick={setLink} active={editor.isActive('link')}>
          <LinkIcon fontSize="small" />
        </Btn>
        <Btn
          title="Remove link"
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive('link')}
        >
          <LinkOff fontSize="small" />
        </Btn>
        <Btn title="Insert image" onClick={triggerImagePick}>
          <ImageIcon fontSize="small" />
        </Btn>
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <Btn
          title="Undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <Undo fontSize="small" />
        </Btn>
        <Btn
          title="Redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <Redo fontSize="small" />
        </Btn>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          hidden
          onChange={handleFileChange}
        />
      </Box>

      <EditorContent editor={editor} className="rte-content" />
    </Box>
  );
};

export default RichTextEditor;
